/**
 * Marketing attribution capture hook — v3
 * Captures UTM, click IDs, influencer ref (?ref=), referrer, landing page,
 * device info, cookie consent, session count, days-to-conversion.
 * Persists in localStorage (survives tab close).
 * First-touch preserved forever; last-touch updated on new UTM/click-ID.
 */
import { useEffect, useCallback } from 'react';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK_IDS = ['gclid', 'fbclid', 'ttclid', 'msclkid', 'li_fat_id', 'twclid'];
const STORAGE_KEY = 'blc_attribution';
const FIRST_TOUCH_KEY = 'blc_attribution_first';
const CONSENT_KEY = 'blc_cookie_consent';
const SESSION_KEY = 'blc_session_ts'; // sessionStorage: timestamp of this session start
const SESSION_GAP_MS = 30 * 60 * 1000; // 30 min inactivity = new session

function detectDevice() {
  const ua = navigator.userAgent || '';
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobi|android|iphone|ipod|blackberry|opera mini|windows phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

function detectOS() {
  const ua = navigator.userAgent || '';
  if (/windows/i.test(ua)) return 'Windows';
  if (/mac os x/i.test(ua) && !/iphone|ipad/i.test(ua)) return 'macOS';
  if (/iphone|ipad|ipod/i.test(ua)) return 'iOS';
  if (/android/i.test(ua)) return 'Android';
  if (/linux/i.test(ua)) return 'Linux';
  return 'Unknown';
}

function detectBrowser() {
  const ua = navigator.userAgent || '';
  if (/chrome/i.test(ua) && !/edge|opr/i.test(ua)) return 'Chrome';
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
  if (/edge/i.test(ua)) return 'Edge';
  if (/opr|opera/i.test(ua)) return 'Opera';
  return 'Other';
}

function inferSource(data) {
  if (data.utm_source) return;
  const ref = data.referrer || '';
  if (!ref) { data.utm_source = 'direct'; return; }
  if (/google\./i.test(ref)) { data.utm_source = 'google'; data.utm_medium = data.utm_medium || 'organic'; }
  else if (/bing\.|yahoo\./i.test(ref)) { data.utm_source = 'bing'; data.utm_medium = 'organic'; }
  else if (/facebook\.|fb\./i.test(ref)) { data.utm_source = 'facebook'; data.utm_medium = 'social'; }
  else if (/instagram\./i.test(ref)) { data.utm_source = 'instagram'; data.utm_medium = 'social'; }
  else if (/tiktok\./i.test(ref)) { data.utm_source = 'tiktok'; data.utm_medium = 'social'; }
  else if (/t\.co|twitter\./i.test(ref)) { data.utm_source = 'twitter'; data.utm_medium = 'social'; }
  else if (/pinterest\./i.test(ref)) { data.utm_source = 'pinterest'; data.utm_medium = 'social'; }
  else if (/youtube\./i.test(ref)) { data.utm_source = 'youtube'; data.utm_medium = 'social'; }
  else if (/mail\.|gmail\.|yahoo\.|outlook\./i.test(ref)) { data.utm_source = 'email'; data.utm_medium = 'email'; }
  else { data.utm_source = 'referral'; data.utm_medium = 'referral'; }
}

export function useAttribution() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const now = new Date().toISOString();
      const nowMs = Date.now();

      // Load persisted attribution
      let existing = {};
      try { existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { /* blocked */ }
      const data = { ...existing };
      let updated = false;

      // ── UTM params (last-touch: overwrite on each new UTM visit) ──
      const hasUtm = UTM_KEYS.some(k => params.has(k));
      if (hasUtm) {
        UTM_KEYS.forEach(k => { if (params.has(k)) { data[k] = params.get(k); updated = true; } });
        data.utm_timestamp = now;
      }

      // ── Click IDs ──
      CLICK_IDS.forEach(k => { if (params.has(k)) { data[k] = params.get(k); updated = true; } });

      // ── Influencer ref param (?ref=NomInfluenceur) ──
      // Influenceurs utilisent: bodylookcare.shop/?ref=PRENOM_INSTA
      const refParam = params.get('ref') || params.get('influencer') || params.get('parrain');
      if (refParam) {
        data.influencer_ref = refParam.trim().toLowerCase();
        // If no UTM source set, infer as influencer
        if (!hasUtm && !data.utm_source) {
          data.utm_source = 'influencer';
          data.utm_medium = 'influencer';
          data.utm_campaign = refParam.trim().toLowerCase();
        }
        updated = true;
      }

      // ── Referrer (first visit) ──
      if (!data.referrer && document.referrer && !document.referrer.includes(window.location.hostname)) {
        data.referrer = document.referrer;
        updated = true;
      }

      // ── Landing page (first visit) ──
      if (!data.landing_page) {
        data.landing_page = window.location.pathname + window.location.search;
        data.first_seen = now;
        data.first_seen_ms = nowMs;
        updated = true;
      }

      // ── Session counting ──
      // A session = visit where last page view was > SESSION_GAP_MS ago (or new tab/browser)
      try {
        const lastTs = parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10);
        const isNewSession = !lastTs || (nowMs - lastTs > SESSION_GAP_MS);
        if (isNewSession) {
          sessionStorage.setItem(SESSION_KEY, String(nowMs));
          data.session_count = (data.session_count || 0) + 1;
          data.last_session_at = now;
          updated = true;
        }
        // Update heartbeat in sessionStorage so we detect 30min inactivity
        sessionStorage.setItem(SESSION_KEY, String(nowMs));
      } catch { /* sessionStorage blocked */ }

      // ── Days since first visit ──
      if (data.first_seen_ms) {
        const daysSince = Math.floor((nowMs - data.first_seen_ms) / (1000 * 60 * 60 * 24));
        if (data.days_since_first_visit !== daysSince) {
          data.days_since_first_visit = daysSince;
          updated = true;
        }
      }

      // ── Pages visited (last 15) ──
      const pages = data.pages_visited || [];
      const currentPage = window.location.pathname;
      if (pages[pages.length - 1] !== currentPage) {
        pages.push(currentPage);
        data.pages_visited = pages.slice(-15);
        data.total_pages_viewed = (data.total_pages_viewed || 0) + 1;
        updated = true;
      }

      // ── Device info ──
      const device = detectDevice();
      const os = detectOS();
      const browser = detectBrowser();
      if (data.device_type !== device || data.os !== os || data.browser !== browser) {
        data.device_type = device;
        data.os = os;
        data.browser = browser;
        data.screen = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
        data.language = navigator.language || '';
        updated = true;
      }

      // ── Cookie consent ──
      try {
        const consent = JSON.parse(localStorage.getItem(CONSENT_KEY) || '{}');
        const cs = consent.all ? 'all' : consent.analytics ? 'analytics' : consent.necessary ? 'necessary' : null;
        if (cs && data.consent_status !== cs) {
          data.consent_status = cs;
          data.consent_date = consent.date || now;
          updated = true;
        }
      } catch { /* blocked */ }

      // ── Infer source from referrer if no UTM or ref ──
      if (!data.utm_source) { inferSource(data); updated = true; }

      if (updated) {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* blocked */ }
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* blocked */ }

        // First-touch — never overwrite
        try {
          if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
            localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({
              utm_source: data.utm_source,
              utm_medium: data.utm_medium,
              utm_campaign: data.utm_campaign,
              utm_term: data.utm_term,
              gclid: data.gclid,
              fbclid: data.fbclid,
              ttclid: data.ttclid,
              influencer_ref: data.influencer_ref,
              referrer: data.referrer,
              landing_page: data.landing_page,
              first_seen: data.first_seen,
            }));
          }
        } catch { /* blocked */ }
      }
    } catch (e) {
      // storage blocked
    }
  }, []); // eslint-disable-line

  const getAttribution = useCallback(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY) || '{}');
      const firstTouch = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || '{}');
      return {
        ...data,
        first_touch: Object.keys(firstTouch).length ? firstTouch : undefined,
      };
    } catch {
      return {};
    }
  }, []);

  return { getAttribution };
}
